import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import { useTecnicos } from '../hooks/useTecnicos';

const ROLES = [
  { value: 'atencion_cliente',        label: 'Atención al Cliente' },
  { value: 'coordinador_operaciones', label: 'Director de Operaciones' },
  { value: 'admin',                   label: 'Administrador' },
  { value: 'tecnico',                 label: 'Técnico' },
];

const ROLE_STYLE = {
  admin:                   { cls: 'bg-red-100 text-red-700',     label: 'Administrador' },
  coordinador_operaciones: { cls: 'bg-blue-100 text-blue-700',   label: 'Director de Operaciones' },
  atencion_cliente:        { cls: 'bg-green-100 text-green-700', label: 'Atención al Cliente' },
  tecnico:                 { cls: 'bg-orange-100 text-orange-700', label: 'Técnico' },
};

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

const EMPTY_FORM = { name: '', email: '', password: '', role: 'atencion_cliente', employeeId: '' };

export default function UsersView() {
  const { user } = useAuth();
  const [users, setUsers]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [modal, setModal]       = useState(null); // null | 'create' | { ...user }
  const [form, setForm]         = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving]     = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // userId | null
  const [deleting, setDeleting] = useState(false);
  const { tecnicos } = useTecnicos();

  const call = useCallback(async (action, payload = {}) => {
    const resp = await fetch('/.netlify/functions/manageUsers', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
      body: JSON.stringify({ action, ...payload }),
    });
    const data = await resp.json();
    if (!data.success) throw new Error(data.error || 'Error desconocido');
    return data.data;
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await call('list');
      setUsers(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setModal('create');
  };

  const openEdit = (u) => {
    setForm({ name: u.name, email: u.email, password: '', role: u.role, employeeId: u.employeeId ? String(u.employeeId) : '' });
    setFormError('');
    setModal(u);
  };

  const closeModal = () => { setModal(null); setFormError(''); };

  const handleSave = async () => {
    if (!form.name.trim())  return setFormError('El nombre es obligatorio');
    if (!form.email.trim()) return setFormError('El email es obligatorio');
    if (modal === 'create' && !form.password) return setFormError('La contraseña es obligatoria para nuevos usuarios');
    if (form.role === 'tecnico' && !form.employeeId) return setFormError('Selecciona el técnico de StelOrder para este rol');

    setSaving(true);
    setFormError('');
    try {
      if (modal === 'create') {
        await call('create', form);
      } else {
        await call('update', { userId: modal.id, ...form });
      }
      await loadUsers();
      closeModal();
    } catch (e) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await call('delete', { userId: confirmDelete });
      await loadUsers();
      setConfirmDelete(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">

      {/* Cabecera */}
      <div className="bg-white rounded-2xl shadow-sm border p-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Gestión de Usuarios</h2>
          <p className="text-sm text-gray-500 mt-0.5">Crea y administra los accesos al sistema</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm"
        >
          <span className="text-lg leading-none">+</span>
          Nuevo usuario
        </button>
      </div>

      {/* Error global */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <span>⚠️</span><span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-3 py-16 text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-200 border-t-red-500 rounded-full animate-spin" />
            Cargando usuarios…
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">👤</div>
            <p className="font-medium">No hay usuarios en Firestore todavía</p>
            <p className="text-sm mt-1">Los usuarios hardcodeados siguen activos hasta que los migres aquí</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wider px-6 py-3">Usuario</th>
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wider px-6 py-3">Email</th>
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wider px-6 py-3">Rol</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => {
                const rs = ROLE_STYLE[u.role] || { cls: 'bg-gray-100 text-gray-600', label: u.role };
                return (
                  <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                          {initials(u.name)}
                        </div>
                        <span className="font-semibold text-gray-800 text-sm">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{u.email}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${rs.cls}`}>
                        {rs.label}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(u)}
                          className="px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => setConfirmDelete(u.id)}
                          className="px-3 py-1.5 text-xs font-semibold text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>


      {/* Modal crear / editar */}
      {modal !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">
                {modal === 'create' ? 'Nuevo usuario' : `Editar — ${modal.name}`}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-xl leading-none">✕</button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
                  {formError}
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Nombre completo</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                  placeholder="Nombre Apellido"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                  placeholder="email@climatizacionzapata.es"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                  Contraseña {modal !== 'create' && <span className="font-normal text-gray-400">(dejar vacío para no cambiarla)</span>}
                </label>
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200"
                  placeholder={modal === 'create' ? 'Contraseña' : 'Nueva contraseña (opcional)'}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Rol</label>
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value, employeeId: '' }))}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 bg-white"
                >
                  {ROLES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>

              {form.role === 'tecnico' && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                    Técnico en StelOrder <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.employeeId}
                    onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 bg-white"
                  >
                    <option value="">— Seleccionar técnico —</option>
                    {tecnicos.map(t => (
                      <option key={t.id} value={String(t.employeeId)}>{t.nombre} (ID: {t.employeeId})</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">Vincula este usuario con su ID en StelOrder para ver sus órdenes.</p>
                </div>
              )}
            </div>

            <div className="px-6 pb-5 flex gap-3 justify-end">
              <button
                onClick={closeModal}
                className="px-4 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors"
              >
                {saving && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                {modal === 'create' ? 'Crear usuario' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal confirmar eliminación */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="text-4xl mb-3">🗑️</div>
            <h3 className="font-bold text-gray-900 mb-2">¿Eliminar usuario?</h3>
            <p className="text-sm text-gray-500 mb-6">Esta acción no se puede deshacer. El usuario perderá el acceso al sistema.</p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-xl transition-colors"
              >
                {deleting && <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
